import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { useStrategyPerformance } from '@/hooks/queries/useAnalytics';
import { formatCurrency, formatStrategyType } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const COLORS = [
  '#0ea5e9', // sky
  '#8b5cf6', // violet
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

export function StrategyBreakdown() {
  const { data: strategies, isLoading } = useStrategyPerformance();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance by Strategy</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  const filteredStrategies = strategies?.filter((s) => s.tradesCount > 0) || [];

  if (filteredStrategies.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance by Strategy</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={<NoDataIcon />}
            title="No data yet"
            description="Close some trades to see strategy breakdown"
          />
        </CardContent>
      </Card>
    );
  }

  const chartData = filteredStrategies.map((s) => ({
    name: formatStrategyType(s.strategyType),
    value: s.tradesCount,
    pnl: s.totalPnl,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance by Strategy</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {chartData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
                        <p className="font-medium">{data.name}</p>
                        <p className="text-sm text-gray-600">{data.value} trades</p>
                        <p className="text-sm">
                          P&L:{' '}
                          <span className={data.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(data.pnl)}
                          </span>
                        </p>
                      </div>
                    );
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Strategy</TableHead>
                  <TableHead className="text-center">Trades</TableHead>
                  <TableHead className="text-center">Win Rate</TableHead>
                  <TableHead className="text-right">P&L</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStrategies.map((strategy) => (
                  <TableRow key={strategy.strategyType}>
                    <TableCell>
                      <StrategyBadge strategy={strategy.strategyType} size="sm" />
                    </TableCell>
                    <TableCell className="text-center">{strategy.tradesCount}</TableCell>
                    <TableCell className="text-center">
                      <span
                        className={cn(
                          'font-medium',
                          (strategy.winRate ?? 0) >= 50 ? 'text-green-600' : 'text-yellow-600'
                        )}
                      >
                        {(strategy.winRate ?? 0).toFixed(0)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <PnlDisplay value={strategy.totalPnl} size="sm" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
